/**
 * Convierte los eventos de reprogramación del planificador multi-día en algo que
 * el usuario pueda leer: **clientes**, agrupados por el día al que llegaron.
 *
 * Existe porque `rolled_over` es un diario, no una lista. El motor emite un evento
 * cada vez que una orden no cabe en un día y la mueve al siguiente, así que una
 * orden que rueda cuatro días genera cuatro entradas casi idénticas. Medido contra
 * el motor real (30 órdenes, un camión, 5 días): **71 eventos para 24 órdenes**,
 * con doce órdenes repetidas cuatro veces cada una.
 *
 * Dos reglas hacen el trabajo:
 *
 * 1. **Sobrevive el último salto de cada cadena.** Es el único que dice a dónde
 *    llegó la orden. Verificado sobre esa misma corrida: de las 16 reprogramadas
 *    que sí se sirvieron, el `next_eligible_date` del último evento coincidió con
 *    el día real de entrega 16 de 16 veces.
 *
 * 2. **Las que terminaron sin ningún día quedan fuera.** En la corrida medida, 8
 *    de las 24 aparecían a la vez acá ("entra el jueves") y en el bloque de no
 *    planificadas ("sin día asignado"): dos avisos del mismo cliente que se
 *    contradicen. El bloque rojo las explica mejor, porque tiene el motivo final.
 */

/** Un evento de reprogramación tal como lo manda el servicio. */
export interface RolloverEvent {
  order_id: string;
  reason: string;
  next_eligible_date: string;
}

/** Un día del horizonte. Solo interesa su fecha y sus eventos. */
export interface HorizonDay {
  date: string;
  rolled_over?: RolloverEvent[];
}

/** Una orden que no entró en ningún día. Solo interesa su identificador. */
export interface UnplannedRef {
  order_id: string;
}

/** Lo mínimo para nombrar a un cliente. */
export interface NamedClient {
  _id: string;
  commercialName?: string;
  name?: string;
}

/** Un cliente reprogramado, ya resuelto. */
export interface RolloverItem {
  id: string;
  name: string;
  reason: string;
  /** Cuántos días rodó antes de encontrar lugar. `1` = un solo salto. */
  rolledDays: number;
}

/** Los clientes que llegan a un mismo día destino. */
export interface RolloverGroup {
  date: string;
  items: RolloverItem[];
}

const GENERIC = 'No entró ese día';

/**
 * El motivo redactado **en clave de reprogramación**: de qué día no pudo salir.
 *
 * Separado de `REASON_LABELS` a propósito. Aquellas etiquetas cierran con un
 * consejo —`shift_full` dice "probar otro día"— que es correcto en el bloque de no
 * planificadas y absurdo acá: el planificador ya probó otro día por su cuenta. La
 * etiqueta le pediría al usuario una acción ya ejecutada.
 */
export function rolloverReasonText(reason: string, fromDate: string): string {
  switch (reason) {
    case 'no_capacity':
      return `No hubo capacidad para él el ${fromDate}`;
    case 'shift_full':
      return `La jornada del ${fromDate} ya estaba completa`;
    case 'window':
      return `Su ventana horaria no encajaba el ${fromDate}`;
    case 'out_of_range':
      return `Quedaba fuera de alcance para la jornada del ${fromDate}`;
    case 'exceeds_capacity':
      return 'Pesa más que el mayor vehículo y no se pudo dividir';
    case 'unreachable':
      return 'No se pudo rutear hasta su ubicación';
    case 'invalid_location':
      return 'Su coordenada no puede ser real — revisar la geolocalización';
    case 'swapped_location':
      return 'Su latitud y longitud parecen intercambiadas';
    case 'other':
      return `El motor no pudo acomodarlo el ${fromDate}`;
    default:
      // Un motivo que el servicio agregue y esta tabla todavía no conozca. Nunca se
      // muestra el código crudo.
      return GENERIC;
  }
}

/**
 * @param days Los días del horizonte, con sus eventos.
 * @param unplanned Las órdenes que no entraron en ningún día: se descartan.
 * @param clients Los clientes cargados en pantalla, para poner nombre.
 */
export function summarizeRollovers(
  days: HorizonDay[] | undefined,
  unplanned: UnplannedRef[] | undefined,
  clients: NamedClient[],
): RolloverGroup[] {
  const neverPlanned = new Set((unplanned ?? []).map((u) => u.order_id));
  const byId = new Map(clients.map((c) => [c._id, c]));

  // El último evento de cada cadena, y cuántos saltos dio. Los días llegan en orden
  // cronológico, así que basta con sobrescribir.
  const last = new Map<string, { from: string; event: RolloverEvent; hops: number }>();
  for (const day of days ?? []) {
    for (const event of day.rolled_over ?? []) {
      if (neverPlanned.has(event.order_id)) continue;
      const hops = (last.get(event.order_id)?.hops ?? 0) + 1;
      last.set(event.order_id, { from: day.date, event, hops });
    }
  }

  const groups = new Map<string, RolloverItem[]>();
  for (const [orderId, { from, event, hops }] of last) {
    const client = byId.get(orderId);
    const items = groups.get(event.next_eligible_date) ?? [];
    items.push({
      id: orderId,
      // Si el cliente no está cargado, el identificador es peor que un nombre pero
      // mejor que omitir la fila: la orden se movió igual.
      name: client?.commercialName || client?.name || orderId,
      reason: rolloverReasonText(event.reason, from),
      rolledDays: hops,
    });
    groups.set(event.next_eligible_date, items);
  }

  // Cronológico: los eventos llegan agrupados por día de ORIGEN, así que sin ordenar
  // los destinos salen entremezclados.
  return [...groups.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([date, items]) => ({ date, items }));
}
